import { useState, useId, useEffect } from 'react'
import { HiSearch, HiX } from 'react-icons/hi'
import Select from 'react-select'
import { RecipeCard } from '@client'
// ----------------------------------------------------------------------

const bittersOptions = [
  { value: 'cherry-bark-vanilla', label: 'Cherry Bark Vanilla Bitters' },
  { value: 'orange', label: 'Orange Bitters' },
  { value: 'bolivar', label: 'Bolivar Bitters' },
  { value: 'blackstrap', label: 'Blackstrap Bitters' },
  { value: 'trinity', label: 'Trinity Bitters' },
  { value: 'root-beer', label: 'Root Beer Bitters' },
  { value: 'ginger-allspice', label: 'Ginger Allspice Bitters' },
  { value: 'grapefruit-hibiscus', label: 'Grapefruit Hibiscus Bitters' },
  { value: 'chipotle-cacao', label: 'Chipotle Cacao Bitters' },
  { value: 'the-wolf', label: 'The Wolf Bitters Blend' },
  { value: 'all-day-aromatic-bitters', label: 'All day aromatic bitters' },
  { value: 'pumpkin-spice-bitters-blend', label: 'Pumpkin Spice Bitters Blend' }
]
const seasonsOptions = [
  { value: 'winter', label: 'Winter' },
  { value: 'fall', label: 'Fall' },
  { value: 'spring', label: 'Spring' },
  { value: 'summer', label: 'Summer' }
]
const spiritOptions = [
  { value: 'vodka', label: 'Vodka' },
  { value: 'gin', label: 'Gin' },
  { value: 'aquavit', label: 'Aquavit' },
  { value: 'brandy', label: 'Brandy' },
  { value: 'rum', label: 'Rum' },
  { value: 'agave', label: 'Agave' },
  { value: 'whiskey', label: 'Whisk(e)y' }
]
const styleOptions = [
  { value: 'citrusy', label: 'Citrusy' },
  { value: 'effervescent', label: 'Effervescent' },
  { value: 'spicy', label: 'Spicy' },
  { value: 'spirit-forward', label: 'Spirit Forward' },
  { value: 'bitter', label: 'Bitter' },
  { value: 'herbaceous', label: 'Herbaceous' },
  { value: 'tropical', label: 'Tropical' },
  { value: 'creamy', label: 'Creamy' },
  { value: 'aromatic', label: 'Aromatic' },
  { value: 'low-abv', label: 'Low ABV option' },
  { value: 'non-alcoholic-option', label: 'Non-Alcoholic option' },
  { value: 'hot', label: 'Hot' },
  { value: 'kitchen', label: 'Kitchen' }
]
const productOptions = [
  { value: 'bittercube-cocktail-cedar', label: 'Cocktail Cedar' },
  { value: 'disco-inferno-liquid-heat', label: 'Disco inferno' },
  { value: 'cardamom-seed-extract', label: 'Cardamom Extract' },
  { value: 'heirloom-pineapple-amaro', label: 'Heirloom Pineapple Amaro' },
  { value: 'heirloom-genepy', label: 'Heirloom Genepy' },
  { value: 'heirloom-creme-de-flora', label: 'Heirloom Creme de Flora' },
  { value: 'heirloom-alchermes', label: 'Heirloom Alchermes' },
  { value: 'heirloom-espresso-martini', label: 'Heirloom Espresso Martini Liqueur'},
  { value: 'vanilla-extract', label: 'Vanilla Extract' },
]

const emptyFilters = {
  bitters: null,
  product: null,
  season: null,
  spirit: null,
  style: null
}

export default function CocktailsList({ allRecipes }) {
  const [recipes, setRecipes] = useState(allRecipes)
  const [filters, setFilters] = useState(emptyFilters)
  const [activeFilter, setActiveFilter] = useState(null)
  const [filterString, setFilterString] = useState('')
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  const updateUrl = (updatedFilters) => {
    // Avoid updating URL on initial load
    if (isInitialLoad) {
      setIsInitialLoad(false)
      return
    }

    const queryParams = new URLSearchParams()
    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value) queryParams.set(key, value.value)
    })

    const newUrl = `${window.location.pathname}?${queryParams.toString()}`
    window.history.pushState({}, '', newUrl)
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    const initialFilters = { ...emptyFilters }

    for (const key of Object.keys(emptyFilters)) {
      const queryValue = queryParams.get(key)
      if (queryValue) {
        initialFilters[key] = findFilterOption(key, queryValue)
      }
    }

    setFilters(initialFilters)
    // Additional logic to filter recipes based on initialFilters
  }, [])

  const findFilterOption = (filterKey, queryValue) => {
    switch (filterKey) {
      case 'bitters':
        return bittersOptions.find((option) => option.value === queryValue)
      case 'product':
        return productOptions.find((option) => option.value === queryValue)
      case 'spirit':
        return spiritOptions.find((option) => option.value === queryValue)
      case 'season':
        return seasonsOptions.find((option) => option.value === queryValue)
      case 'style':
        return styleOptions.find((option) => option.value === queryValue)
      default:
        return null
    }
  }

  const findFilterLabel = (filterValue, filterOptions) => {
    const filter = filterOptions.find((option) => option.value === filterValue)
    return filter ? filter.label : null
  }

  useEffect(() => {
    // we only want to update the state if the filters have changed, and they are not the same as the empty filters
    if (
      JSON.stringify(filters) === JSON.stringify(emptyFilters) &&
      !isInitialLoad
    ) {
      clearFilters()
      return
    }

    const activeFilters = Object.values(filters).filter((value) => value)
    setFilterString('')
    if (activeFilters.length === 0) {
      setRecipes(allRecipes)
    } else {
      const filteredRecipes = allRecipes.filter((recipe) =>
        applyFilters(recipe, filters)
      )
      setRecipes(filteredRecipes)
    }

    updateUrl(filters)
  }, [filters])

  const handleFilterString = (searchString) => {
    setFilterString(searchString)

    setFilters(emptyFilters)
    setActiveFilter(null)

    const filtered = allRecipes.filter((recipe) => {
      let tags = []

      if (recipe.name.toLowerCase().includes(searchString.toLowerCase()))
        return true

      if (recipe.bitters) {
        tags.push(...recipe.bitters.map((b) => b.name.toLowerCase()))
      }

      if (recipe.seasons) {
        tags.push(...recipe.seasons.map((s) => s.name.toLowerCase()))
      }

      if (recipe.spirits) {
        tags.push(...recipe.spirits.map((s) => s.name.toLowerCase()))
      }

      if (recipe.cocktail_styles) {
        tags.push(...recipe.cocktail_styles.map((s) => s.name.toLowerCase()))
      }

      return tags.some((tag) => tag.includes(searchString.toLowerCase()))
    })

    setRecipes(filtered)
  }

  const applyFilters = (recipe, filters) => {
    return (
      (!filters.bitters || filterRecipesByBitters(recipe, filters.bitters)) &&
      (!filters.product || filterRecipesByProduct(recipe, filters.product)) &&
      (!filters.spirit || filterRecipesBySpirit(recipe, filters.spirit)) &&
      (!filters.season || filterRecipesBySeason(recipe, filters.season)) &&
      (!filters.style || filterRecipesByStyle(recipe, filters.style))
    )
  }

  const filterRecipesByBitters = (recipe, selected) => {
    return (
      recipe.bitters &&
      recipe.bitters.some(
        (b) => b.name.toLowerCase() === selected.label.toLowerCase()
      )
    )
  }

  const filterRecipesByProduct = (recipe, selected) => {
    return (
      recipe.other_products &&
      recipe.other_products.some(
        (s) => s.name.toLowerCase() === selected.label.toLowerCase()
      )
    )
  }

  const filterRecipesBySpirit = (recipe, selected) => {
    return (
      recipe.spirits &&
      recipe.spirits.some(
        (s) => s.name.toLowerCase() === selected.label.toLowerCase()
      )
    )
  }

  const filterRecipesBySeason = (recipe, selected) => {
    return (
      recipe.seasons &&
      recipe.seasons.some(
        (s) => s.name.toLowerCase() === selected.label.toLowerCase()
      )
    )
  }

  const filterRecipesByStyle = (recipe, selected) => {
    return (
      recipe.cocktail_styles &&
      recipe.cocktail_styles.some(
        (s) => s.name.toLowerCase() === selected.label.toLowerCase()
      )
    )
  }

  const clearFilters = () => {
    setFilters(emptyFilters)
    setRecipes(allRecipes)
    updateUrl(emptyFilters)
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)

    const bittersFilter = searchParams.get('bitters')
    const productsFilter = searchParams.get('product')
    const seasonsFilter = searchParams.get('season')
    const spiritFilter = searchParams.get('spirit')
    const styleFilter = searchParams.get('style')

    if (bittersFilter) {
      setActiveFilter(findFilterLabel(bittersFilter, bittersOptions))
    } else if (productsFilter) {
      setActiveFilter(findFilterLabel(productsFilter, productOptions))
    } else if (seasonsFilter) {
      setActiveFilter(findFilterLabel(seasonsFilter, seasonsOptions))
    } else if (spiritFilter) {
      setActiveFilter(findFilterLabel(spiritFilter, spiritOptions))
    } else if (styleFilter) {
      setActiveFilter(findFilterLabel(styleFilter, styleOptions))
    } else {
      setActiveFilter(null)
    }
  }, [window.location.search])

  useEffect(() => {
    const hash = window.location.hash
    if (hash) {
      const element = document.querySelector(hash)
      if (element) {
        element.scrollIntoView()
      }
    }
  }, [window.location.hash])

  return (
    <>
      <div className='container flex flex-col items-center justify-between py-0 xl:flex-row'>
        <div className='flex items-center w-full gap-2 p-1 my-4 rounded-md bg-paper-action/20 xl:w-[300px]'>
          <HiSearch className='w-5 h-5 text-dark' />
          <input
            type='text'
            name='search'
            placeholder='Search Recipes'
            className='w-full p-2 font-semibold tracking-widest uppercase text-gold bg-paper-action/0'
            value={filterString}
            onChange={(e) => {
              handleFilterString(e.target.value)
            }}
          />
        </div>
        <div className='grid items-center grid-cols-2 gap-3 pb-4 lg:flex xl:pb-0'>
          <span className='col-span-2 label'>Filter By:</span>
          <button
            className='flex items-center justify-center order-last w-full gap-2 py-2 my-0 opacity-50 btn btn-action lg:w-fit hover:opacity-100'
            onClick={clearFilters}
          >
            <span className='text-sm lg:hidden'>Clear Filters</span> <HiX />
          </button>
          <Select
            instanceId={useId()}
            options={bittersOptions}
            placeholder={'Bitters'}
            classNamePrefix='select'
            isClearable={true}
            value={filters.bitters}
            onChange={(selected) => {
              setActiveFilter(selected ? selected.label : null)
              setFilters({ ...emptyFilters, bitters: selected })
            }}
          />
          <Select
            instanceId={useId()}
            options={productOptions}
            placeholder={'Other Products'}
            classNamePrefix='select'
            isClearable={true}
            value={filters.product}
            onChange={(selected) => {
              setActiveFilter(selected ? selected.label : null)
              setFilters({ ...emptyFilters, product: selected })
              // handleFilterChange(selected)
            }}
          />
          <Select
            instanceId={useId()}
            options={seasonsOptions}
            placeholder={'Season'}
            classNamePrefix='select'
            isClearable={true}
            value={filters.season}
            onChange={(selected) => {
              setActiveFilter(selected ? selected.label : null)
              setFilters({ ...emptyFilters, season: selected })
            }}
          />
          <Select
            instanceId={useId()}
            options={spiritOptions}
            placeholder={'Spirit'}
            classNamePrefix='select'
            isClearable={true}
            value={filters.spirit}
            onChange={(selected) => {
              setActiveFilter(selected ? selected.label : null)
              setFilters({ ...emptyFilters, spirit: selected })
            }}
          />
          <Select
            instanceId={useId()}
            options={styleOptions}
            placeholder={'Cocktail Style'}
            classNamePrefix='select'
            isClearable={true}
            value={filters.style}
            onChange={(selected) => {
              setActiveFilter(selected ? selected.label : null)
              setFilters({ ...emptyFilters, style: selected })
            }}
          />
        </div>
      </div>
      <hr />
      <div className='container pt-2 pb-8 text-center' id='recipe-list'>
        <h2 className='mt-4 mb-0 text-2xl'>
          {activeFilter ? `${activeFilter}  Recipes` : ''}
        </h2>
        <span className='text-sm label text-dark/30'>
          Showing {recipes.length} recipes.
        </span>
      </div>
      <div className='container grid gap-6 md:grid-cols-3 lg:grid-cols-4'>
        {recipes &&
          recipes.map((recipe, i) => {
            return <RecipeCard key={`${recipe.slug}_${i}`} recipe={recipe} />
          })}
        {recipes.length === 0 && (
          <p className='text-center h2 md:col-span-2 lg:col-span-3'>
            No Cocktails Found
          </p>
        )}
      </div>
    </>
  )
}
